import useDeviceSubtype from 'hooks/useDeviceSubtype';
import useDeviceType from 'hooks/useDeviceType';
import useGenericDevice from 'hooks/useGenericDevice';
import useIsOnline from 'hooks/useIsOnline';
import { useTranslation } from 'next-i18next';
import { useCallback } from 'react';
import type { FC, ReactElement } from 'react';
import { prettifyDeviceType } from 'utils/common/deviceTypeUtils';
import { DeviceStatus } from 'utils/communication/websocketTypes';

import CustomListItem from 'components/DeviceConfigComponents/CustomListItem';
import DeviceIconComponent from 'components/DeviceIconComponent';
import CustomCheckbox from 'components/MuiCustom/CustomCheckbox';

import { useAppSelector } from 'redux-store';

export interface DeviceListItemProps {
  serial: string;
  deviceId?: number;
  selectable?: boolean;
  selectedDeviceIds?: number[];
  setSelectedDeviceIds?: (selectedDeviceIds: number[]) => void;
  rightComponent?: ReactElement | null;
}

const DeviceList: FC<DeviceListItemProps> = ({
  serial,
  deviceId,
  selectable = false,
  rightComponent,
  selectedDeviceIds = [],
  setSelectedDeviceIds,
}) => {
  const { t } = useTranslation();

  const online = useIsOnline(serial);
  const friendlyName = useGenericDevice(serial, state => state?.fna);
  const type = useDeviceType(serial);
  const subtype = useDeviceSubtype(serial);
  const customName = prettifyDeviceType(type);

  const deviceStatus = useAppSelector(
    state => state.api.fullStatus[serial]?._status,
  );

  const getSubtitle = (): string => {
    if (online) {
      return t('common:serial_number_with_value', {
        value: serial,
      });
    }

    if (deviceStatus === DeviceStatus.InvalidAuth) {
      return t('common:goe.deviceStatus.InvalidAuth');
    }

    return t('common:offline');
  };

  const handleCheckboxClick = useCallback(() => {
    if (deviceId === undefined || !setSelectedDeviceIds) return;
    if (selectedDeviceIds.includes(deviceId)) {
      setSelectedDeviceIds(selectedDeviceIds.filter(id => id !== deviceId));
    } else {
      setSelectedDeviceIds([...selectedDeviceIds, deviceId]);
    }
  }, [deviceId, selectedDeviceIds, setSelectedDeviceIds]);

  return (
    <CustomListItem
      onClick={handleCheckboxClick}
      testId={`device-${serial}`}
      leftComponent={
        <>
          {selectable ? (
            <CustomCheckbox
              checked={
                deviceId !== undefined
                  ? selectedDeviceIds.includes(deviceId)
                  : false
              }
              onChange={handleCheckboxClick}
              data-testid="select-item-checkbox"
            />
          ) : null}
          <DeviceIconComponent typ={type} subtype={subtype} outlined />
        </>
      }
      leftText={friendlyName || `${customName}_${serial}`}
      subtitle={getSubtitle()}
      rightComponent={rightComponent}
      backgroundColor="transparent"
    />
  );
};

export default DeviceList;
